import { Add, Edit, Search } from "@mui/icons-material";
import { Button, Stack, Typography } from "@mui/material";
import { MedicationForm } from "../MedicationForm";
import { useInterventionsContext } from "../../../../contexts/InterventionsContext";
import { toast } from "react-toastify";
import { SyntheticEvent } from "react";
import {
  RECEITUARIO_TIPO_SELECT_AUTO,
  stampReceituarioProfile,
  type ReceituarioModelo,
  type ReceituarioTipoSelectValue,
} from "src-next/modules/sncr";

export function PrescriptionCare({
  setSelect,
  clean,
  hideProtocolSelect = false,
  /**
   * Remissão na agenda: false bloqueia adicionar itens novos (só edita existentes).
   * Item novo não tem `refact` e ficaria fora do PDF reemitido. Padrão true = atendimento normal.
   */
  allowAdd = true,
}: any) {
  const {
    control,
    medicationOptions,
    action,
    setValue,
    handleSubmit,
    setClean,

    isEdit,
    setMedicationTabValue,
    medicationTabValue,
  } = useInterventionsContext();
  // Sem item selecionado na lista, o submit seria "adicionar" — bloqueado quando allowAdd=false
  const addBlocked = !allowAdd && !isEdit;

  // Função para retornar o tipo baseado na aba selecionada
  // Usado tanto nas filtragens quanto na geração de PDFs separados
  function getTypeByTabValue(tabValue: number) {
    switch (tabValue) {
      case 0:
        return "medication";
      case 1:
        return "supplement";
      case 2:
        return "herbalMedicine";
      default:
        return "medication";
    }
  }

  const onSubmit = async (data: any) => {
    try {
      if (!data.medication) {
        toast.warn("Selecione algum item pra efetuar a prescrição");
        throw new Error("Formulário vazio");
      } else {
        // Hidratação da reemissão grava quantity.value numérico. String evita o
        // .match do builder FHIR na assinatura; escolha no catálogo continua string.
        const quantity =
          data?.quantity != null && typeof data.quantity === "object"
            ? (data.quantity.label ?? data.quantity.value)
            : data?.quantity != null
              ? String(data.quantity)
              : data?.quantity;

        const auxForm = [
          quantity && `${quantity}`,
          data?.form?.label && `${data?.form?.label}`,
          data?.frequency?.label && `a cada ${data?.frequency?.label}`,
          data?.time?.label && `por ${data?.time?.label}`,
        ]
          ?.filter((e: string) => e)
          ?.join(" ");

        // Fora do catálogo (reemissão): não reduzir a nome/label — perde code e tarja.
        const inputValue =
          medicationOptions?.find(
            (e: any) => e?.value === data?.medication?.value,
          ) || data?.medication;

        const tipoSelect = (data?.receituarioTipo ||
          RECEITUARIO_TIPO_SELECT_AUTO) as ReceituarioTipoSelectValue;
        const isManual = tipoSelect !== RECEITUARIO_TIPO_SELECT_AUTO;

        const auxItem = {
          name: data?.medication?.name,
          label: data?.medication?.label,
          tag: data?.specialControl
            ? "Controle especial"
            : data?.medication?.tag,
          class:
            data?.medication?.class ||
            inputValue?.class ||
            undefined,
          inputValue,
          quantity,
          frequency: data?.frequency,
          formValue: data?.form,
          time: data?.time,
          note: data?.note,
          form: auxForm,
          type: getTypeByTabValue(medicationTabValue),
          code: data?.medication?.code,
          tabValue: `${medicationTabValue}`,
          ...(isManual
            ? {
                receituarioTipoManual: true,
                receituarioModelo: tipoSelect as ReceituarioModelo,
              }
            : {
                receituarioTipoManual: false,
              }),
        };

        action(stampReceituarioProfile(auxItem));
      }
    } catch (err) {
      console.log("err", err);
    } finally {
      setClean(true);
    }
  };

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setMedicationTabValue(newValue);
  };

  return (
    <Stack
      width="100%"
      maxHeight={hideProtocolSelect ? "75vh" : "60vh"}
      overflow="auto"
    >
      {!hideProtocolSelect ? (
        <Button
          sx={{ width: "fit-content", textTransform: "none" }}
          startIcon={<Search />}
          onClick={() => setSelect(true)}
        >
          Selecionar protocolo salvo
        </Button>
      ) : null}
      <MedicationForm
        tabValue={medicationTabValue}
        handleChange={handleChange}
        render={!clean}
        control={control}
        setValue={setValue}
      />

      <Stack
        width="100%"
        direction="row"
        mt={2}
        mb={2}
        justifyContent="right"
        alignItems="center"
        spacing={1}
      >
        {addBlocked ? (
          <Typography variant="body2" color="text.secondary">
            Selecione um item da lista para editar
          </Typography>
        ) : null}
        <Button
          sx={{ width: "fit-content", border: "1px solid #007C6D" }}
          onClick={handleSubmit(onSubmit)}
          variant="text"
          disabled={addBlocked}
        >
          {isEdit ? <Edit /> : <Add />}
        </Button>
      </Stack>
    </Stack>
  );
}
